// src/pages/portal/ClientPortalSidebar.jsx

import React, { useEffect, useMemo, useState, useCallback } from "react";
import { NavLink, useSearchParams } from "react-router-dom";
import { Box, Typography, Divider, Paper } from "@mui/material";
import axios from "axios";
import { useActAsClient } from "./ActAsClientContext";

// ✅ BOTH auth instances
import { auth } from "../../firebase-config";
import { authPortal } from "../../firebase-portal";

const GET_PORTAL_BRANDING_URL =
  process.env.REACT_APP_GET_PORTAL_BRANDING_URL ||
  "https://getportalbranding-774073159618.us-central1.run.app";

const GET_CLIENT_PROFILE_URL =
  process.env.REACT_APP_GET_CLIENT_PROFILE_URL ||
  "https://getclientprofile-774073159618.us-central1.run.app";

// Base (non-active) link style
const linkStyle = {
  display: "block",
  padding: "10px 12px",
  borderRadius: 8,
  textDecoration: "none",
  color: "#333",
  fontSize: 14,
};

export default function ClientPortalSidebar({ onNavigate }) {
  const [branding, setBranding] = useState({
    orgName: "Client Portal",
    accentColor: "#1e40af",
    logoUrl: "",
  });
  const [clientName, setClientName] = useState("");

  const actAs = useActAsClient();
  const [params] = useSearchParams();

  // ✅ Shared helper: choose correct auth + headers
  const getAuthContext = useCallback(async () => {
    const isActingAs = !!actAs?.isActingAs;

    const currentUser = isActingAs ? auth.currentUser : authPortal.currentUser;
    if (!currentUser) throw new Error(isActingAs ? "No staff user" : "No portal user");

    const idToken = await currentUser.getIdToken(true);
    const actAsHeaders = isActingAs ? actAs.getActAsHeaders() : {};
    const headers = { Authorization: `Bearer ${idToken}`, ...actAsHeaders };

    return { isActingAs, currentUser, idToken, headers };
  }, [actAs]);

  useEffect(() => {
    // Guard for act-as mode until context ready
    const isExpectingActAs = params.has("actAsClient") || params.has("as");
    if (isExpectingActAs && !actAs.isActingAs) return;

    (async () => {
      try {
        const { headers } = await getAuthContext();

        // Load branding
        const bResp = await axios.post(GET_PORTAL_BRANDING_URL, {}, { headers });
        const raw = bResp?.data || {};

        const orgName =
          raw.orgName ||
          raw.organizationName ||
          raw?.org?.organizationName ||
          "Client Portal";

        const accentColor =
          raw.accentColor ||
          raw?.branding?.accentColor ||
          "#1e40af";

        const logoUrl =
          raw.logoUrl ||
          raw?.branding?.logoUrl ||
          raw?.branding?.logoURL ||
          "";

        setBranding({ orgName, accentColor, logoUrl });

        // Load client profile (for welcome line)
        try {
          const pResp = await axios.post(GET_CLIENT_PROFILE_URL, {}, { headers });
          const fullName = pResp?.data?.profile?.fullName || "";
          setClientName(fullName);
        } catch (profileError) {
          console.warn("[ClientPortalSidebar] profile load failed:", profileError);
          setClientName("");
        }
      } catch (e) {
        console.warn("[ClientPortalSidebar] load failed:", e);
      }
    })();
  }, [actAs, params, getAuthContext]);

  // Accent-aware active nav style
  const activeStyle = useMemo(
    () => ({
      ...linkStyle,
      background: "#EEF2FF",
      color: branding.accentColor || "#1e40af",
      fontWeight: 600,
      border: `1px solid ${(branding.accentColor || "#1e40af")}22`,
    }),
    [branding.accentColor]
  );

  const handleNavClick = () => {
    if (onNavigate) onNavigate();
  };

  return (
    <Box
      sx={{
        borderRight: "1px solid #e5e7eb",
        p: 2,
        bgcolor: "white",
        display: "flex",
        flexDirection: "column",
        gap: 2,
        minHeight: { xs: "100%", md: "100vh" },
        boxSizing: "border-box",
      }}
    >
      {/* Staff "acting as" ribbon */}
      {actAs?.isActingAs && (
        <Box
          sx={{
            p: 1,
            borderRadius: 1,
            bgcolor: "#FFFBEB",
            border: "1px solid #FCD34D",
          }}
        >
          <Typography variant="caption" sx={{ fontWeight: 700, color: "#92400E" }}>
            Staff preview: acting as{" "}
            <span style={{ textDecoration: "underline" }}>
              {actAs.actAsName || actAs.clientId}
            </span>
            .
          </Typography>
        </Box>
      )}

      {/* Brand header */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        {branding.logoUrl ? (
          <img
            src={branding.logoUrl}
            alt={branding.orgName}
            style={{
              height: 72,
              width: "auto",
              display: "block",
              objectFit: "contain",
            }}
          />
        ) : (
          <Typography variant="h6" sx={{ m: 0 }}>
            {branding.orgName}
          </Typography>
        )}
      </Box>

      {/* Friendly welcome card */}
      <Paper
        variant="outlined"
        sx={{
          p: 1.5,
          borderRadius: 2,
          borderColor: `${branding.accentColor}33`,
          bgcolor: `${branding.accentColor}14`,
        }}
      >
        <Typography
          variant="subtitle1"
          sx={{ m: 0, fontWeight: 800, color: branding.accentColor }}
        >
          {actAs.isActingAs
            ? "Staff Preview"
            : clientName
            ? `Welcome, ${clientName}!`
            : "Welcome!"}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {actAs.isActingAs ? "Viewing as client." : "Use the menu to access your items."}
        </Typography>
      </Paper>

      <Divider />

      <nav>
        <NavLink
          to="/portal"
          end
          style={({ isActive }) => (isActive ? activeStyle : linkStyle)}
          onClick={handleNavClick}
        >
          Overview
        </NavLink>
        <NavLink
          to="/portal/documents"
          style={({ isActive }) => (isActive ? activeStyle : linkStyle)}
          onClick={handleNavClick}
        >
          Documents
        </NavLink>
        <NavLink
          to="/portal/forms"
          style={({ isActive }) => (isActive ? activeStyle : linkStyle)}
          onClick={handleNavClick}
        >
          Forms To Do
        </NavLink>
        <NavLink
          to="/portal/forms/completed"
          style={({ isActive }) => (isActive ? activeStyle : linkStyle)}
          onClick={handleNavClick}
        >
          Completed Forms
        </NavLink>
        <NavLink
          to="/portal/info"
          style={({ isActive }) => (isActive ? activeStyle : linkStyle)}
          onClick={handleNavClick}
        >
          My Information
        </NavLink>
      </nav>

      <Box sx={{ mt: "auto", pt: 2 }}>
        <Divider sx={{ mb: 1 }} />
        <Typography variant="caption" color="text.secondary">
          © {new Date().getFullYear()} {branding.orgName}
        </Typography>
      </Box>
    </Box>
  );
}